import { searchAutocompleteAPITrigger } from '@api/form-verification';
import CapturePic from '@components/capture-pic';
import UploadFile from '@components/file-upload';
import FileTile from '@components/ui/file-tile';
import AutocompleteField from '@components/ui/inputs/autocomplete-field';
import SearchAutocomplete from '@components/ui/inputs/autocomplete-infinite-scroll-field';
import BlockTextfield from '@components/ui/inputs/block-textfield';
import DateField from '@components/ui/inputs/date-field';
import StandardTextfield from '@components/ui/inputs/standard-textfield';
import YearField from '@components/ui/inputs/year-field';
import { PREDEFINED_OPTIONS } from '@containers/form-verification/verification/constants';
import { VerificationContextType } from '@containers/form-verification/verification/context';
import { isRequiredInput } from '@containers/form-verification/verification/field/helpers';
import { HandleChangeArgs } from '@containers/form-verification/verification/types';
import { AllowedFileType } from '@global/constants/general';
import { getDateToRender, validateFiles } from '@global/helpers/general';
import { Box, Button, Grid, SxProps, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import AlertComponents from './alert-components';
import HelperTextJSX from './helper-text-jsx';
import styles from './styles';
import { OptionType } from './types';
const fileSizeLimitMB = +import.meta.env.VITE_FILE_SIZE_MAX_LIMIT / 1000000;
const fileSizeMinLimitKB = +import.meta.env.VITE_FILE_SIZE_MIN_LIMIT / 1000;
interface DynamicInputFieldProps {
  input: any;
  fieldIdx: number;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  value: any;
  handleChange: (args: HandleChangeArgs) => void;
  handleEnterKeyPress?: (e: any, type: any) => void;
  showAddOtherInAutocomplete?: boolean;
  sx?: SxProps;
  fields?: VerificationContextType['states']['fields'];
  verificationName?: string;
  helperTextIcon?: string;
}

function DynamicInputField(props: DynamicInputFieldProps) {
  const {
    input,
    fieldIdx,
    disabled = false,
    error = false,
    helperText = '',
    value,
    handleChange,
    handleEnterKeyPress = () => {},
    showAddOtherInAutocomplete = false,
    sx = {},
    fields = [],
    verificationName = '',
    helperTextIcon
  } = props;
  const inputOptions: OptionType[] = input?.optionsKey
    ? PREDEFINED_OPTIONS[input?.optionsKey]
    : input?.options;
  const selectedOption = useMemo(() => {
    if (input?.inputType !== 'select' || !inputOptions) return null;
    return inputOptions.find((opt: any) => opt?.value === value) ?? null;
  }, [input?.inputType, inputOptions, value]);
  const [filePickerOpen, setFilePickerOpen] = useState<boolean>(false);
  const [openCamera, setOpenCamera] = useState<boolean>(false);

  // Helper function to create handleChange args object
  const createHandleChangeArgs = (
    value: any,
    additionalArgs?: { nested?: boolean, extraAttributes?: any }
  ): HandleChangeArgs => ({
    name: input?.fieldName,
    value,
    fieldIdx,
    clearOtherFields: input?.clearOtherFields ?? [],
    ...(additionalArgs?.nested !== undefined && { nested: additionalArgs.nested }),
    ...(additionalArgs?.extraAttributes && { extraAttributes: additionalArgs.extraAttributes })
  });
  const renderDynamicContent = () => {
    switch (input?.inputType) {
      case 'block':
        return (
          <Box>
            <BlockTextfield
              input={input}
              updateField={(val) => {
                handleChange(createHandleChangeArgs(val));
              }}
              errorText={helperText}
            />
          </Box>
        );
      case 'date':
        return (
          <DateField
            size="medium"
            placeholder={input?.label}
            sx={{ width: '100%' }}
            value={value ? getDateToRender(value) : null}
            onChange={(val) => handleChange(createHandleChangeArgs(val))}
            error={error}
            helperText={helperText}
            disabled={disabled}
            disableFuture
          />
        );
      case 'select':
        return (
          <AutocompleteField
            key={input?.fieldName}
            sx={{ width: '100%' }}
            options={inputOptions}
            placeholder={input?.label}
            error={error}
            helperText={helperText}
            variant={'outlined'}
            shrinkLabel={true}
            value={selectedOption}
            onChange={(_e, val) => {
              handleChange(createHandleChangeArgs(val?.value));
            }}
            getOptionLabel={(r) => {
              return r?.name;
            }}
            isOptionEqualToValue={(option, value) => {
              return option?.value === value?.value;
            }}
            disabled={disabled || input?.disabled}
            onKeyDown={(e) => {
              handleEnterKeyPress(e, '');
            }}
          />
        );
      case 'autocomplete':
        return (
          <SearchAutocomplete
            disabled={disabled}
            showAddOther={showAddOtherInAutocomplete}
            sx={{ width: '100%', margin: '0em 0 0 0' }}
            selectedKey={value ?? (input?.allowMultiple ? [] : '')}
            updateSelectedKey={(event) => {
              handleChange(
                createHandleChangeArgs(event?.value ?? (input?.allowMultiple ? event : ''))
              );
            }}
            apiTrigger={async (search, newSeedValue) => {
              if (input?.options) {
                return { list: input?.options, total: input?.options?.length };
              }
              const res = await searchAutocompleteAPITrigger(
                search,
                newSeedValue,
                input,
                fields,
                verificationName
              );
              return res;
            }}
            getName={(option) => {
              return { primary: option.name };
            }}
            initialText={'Select an option'}
            other={{ name: 'Other', value: 'Other' }}
            error={error}
            helperText={
              input?.helperTextJSXKey ? <HelperTextJSX input={input} value={value} /> : helperText
            }
            others={input?.others}
            allowMultiple={input?.allowMultiple}
            maxLimit={input?.maxLimit}
            helperTextIcon={helperTextIcon}
          />
        );
      case 'file':
        return (
          <>
            {!value && (
              <UploadFile
                openOnMount={filePickerOpen}
                acceptMultipleFiles={false}
                label={`Click to upload file (${fileSizeMinLimitKB}KB - ${fileSizeLimitMB}MB)`}
                handleFiles={(files) => {
                  const validFiles = validateFiles([files[0]], Object.values(AllowedFileType));
                  if (validFiles?.length === 0) {
                    return;
                  }
                  handleChange(createHandleChangeArgs(files[0]));
                }}
                error={error}
                sx={styles.uploadFile}
                disabled={disabled || value}
                fileTypes={input?.allowedFileTypes}
              />
            )}
            {value && (
              <Grid container>
                <Grid item xs={11.8} sm={5.85}>
                  <FileTile
                    shouldProcess={input?.shouldRunOcrCheck}
                    setFilePickerOpen={setFilePickerOpen}
                    file={value}
                    deleteFile={() => {
                      handleChange(createHandleChangeArgs(null));
                    }}
                    allowedFileTypes={input?.allowedFileTypes ?? []}
                    disabled={disabled}
                    label={value?.name ?? input.fieldName}
                    url={typeof value === 'string' ? value : undefined}
                  />
                </Grid>
              </Grid>
            )}
          </>
        );
      case 'live-cam-with-geolocation':
        return (
          <>
            {value ? (
              // Show image preview with remove button if image exists
              <Box sx={{ textAlign: 'center', padding: 1 }}>
                <Box
                  sx={{
                    marginBottom: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    overflow: 'hidden',
                    maxWidth: 300,
                    margin: '0 auto 16px auto'
                  }}>
                  <img
                    src={typeof value === 'string' ? value : URL.createObjectURL(value)}
                    alt="Captured"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                </Box>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => {
                    handleChange(createHandleChangeArgs(null));
                  }}
                  disabled={disabled}>
                  Remove Image
                </Button>
              </Box>
            ) : (
              // Show Add Image button if no image exists
              <Box sx={{ textAlign: 'center', padding: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setOpenCamera(true);
                  }}
                  disabled={disabled}>
                  Add Image
                </Button>
              </Box>
            )}

            {/* Camera modal - only shown when openCamera is true and no image exists */}
            {openCamera && !value && (
              <CapturePic
                open={openCamera}
                title={input?.label}
                setOpen={setOpenCamera}
                handleProceed={async (image, setLoading, extraAttributes) => {
                  await handleChange(
                    createHandleChangeArgs(image, { nested: false, extraAttributes })
                  );
                  setOpenCamera(false);
                }}
                acceptGeolocation
              />
            )}
          </>
        );

      case 'year':
        return (
          <YearField
            value={value ?? ''}
            onChange={(e: any) =>
              handleChange(
                createHandleChangeArgs(e.target.value, {
                  nested: input?.nested ? true : false
                })
              )
            }
            disabled={disabled}
            type={input?.inputType ?? 'text'}
          />
        );
      default:
        return (
          <Box>
            <StandardTextfield
              placeholder={input.placeholder || input.label}
              sx={{ width: '100%' }}
              error={error}
              helperText={
                input?.helperTextJSXKey ? <HelperTextJSX input={input} value={value} /> : helperText
              }
              helperTextIcon={helperTextIcon}
              value={value ?? ''}
              onChange={(e) =>
                handleChange(
                  createHandleChangeArgs(e.target.value, {
                    nested: input?.nested ? true : false
                  })
                )
              }
              disabled={disabled}
              type={input?.inputType ?? 'text'}
              maxLength={input?.maxLength ?? null}
            />
          </Box>
        );
    }
  };

  return (
    <Box sx={sx}>
      {/* sectionTitle - used to show subheadings like 'Local Address'*/}
      {input.sectionTitle && (
        <Box sx={styles.sectionTitle}>
          <Typography variant="body1" sx={{ fontWeight: '700' }}>
            {input.sectionTitle}
          </Typography>
          <Box sx={{ height: '1px', flex: 1, backgroundColor: 'border.lighter' }} />
        </Box>
      )}
      {input?.alert ? (
        input?.alert?.componentKey ? (
          <AlertComponents alert={input?.alert} />
        ) : (
          input?.alert?.text
        )
      ) : null}
      {!input?.fieldName?.includes('ekycOtp') && (
        <>
          {!input?.hideLabel && (
            <Typography variant="body2" sx={styles.fieldLabel}>
              {input.label}
              {input?.inputType === 'file'
                ? ` (${fileSizeMinLimitKB}KB - ${fileSizeLimitMB}MB)`
                : ''}
              {!isRequiredInput(input, fields[fieldIdx], verificationName) && (
                <Box sx={{ color: 'border.dark' }} component={'span'}>
                  {' (Optional)'}
                </Box>
              )}
            </Typography>
          )}
          <Box sx={styles.fieldsWrapper}>
            <Box
              sx={{
                flex: 1
              }}>
              {renderDynamicContent()}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

export default DynamicInputField;
